import { ButtonComponent } from 'baseComponents/ButtonComponent';
import dayjs from 'dayjs';
import { TableComponent } from 'baseComponents/TableComponent';
import { AdminPageWrapper } from 'components/layouts/AdminPageWrapper';
import { AdminErrorPage } from 'components/AdminErrorPage';
import { UserType, useGetUsers } from 'hooks/useGetUsers';
import { ColumnsType } from 'antd/es/table';
import { tableSorters } from 'baseComponents/TableComponent/table.utils';
import { TableFilter } from 'baseComponents/TableComponent/TableFilter/TableFilter';
import { EllipsisComponent } from 'baseComponents/EllipsisComponent';
import { useLocation, useNavigate } from 'react-router';
import { notification } from 'antd';
import { useEffect } from 'react';

const columns: ColumnsType<UserType> = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    sorter: tableSorters.string('email'),
    sortDirections: ['descend', 'ascend'],
    onFilter: (value, record) =>
      record.email
        ? record.email.toLowerCase().includes(value.toString().toLowerCase())
        : false,
    filterDropdown: (args) => <TableFilter dataIndex='email' {...args} />,
  },
  {
    title: 'Store Names',
    dataIndex: 'stores',
    key: 'store_names',
    render: (stores) =>
      stores.length > 0 ? (
        <EllipsisComponent maxWidth={200}>
          {stores.map((store: any) => store?.name).join(', ')}
        </EllipsisComponent>
      ) : (
        '-'
      ),
  },
  {
    title: 'Store IDs',
    dataIndex: 'stores',
    key: 'store_ids',
    render: (stores) =>
      stores.length > 0 ? (
        <EllipsisComponent maxWidth={100}>
          {stores.map((store: any) => store?.id).join(', ')}
        </EllipsisComponent>
      ) : (
        '-'
      ),
  },
  {
    title: 'Last updated',
    dataIndex: 'updated_at',
    key: 'updated_at',
    render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : ''),
  },
  {
    title: 'Last updated by:',
    dataIndex: 'updated_by',
    key: 'updated_by',
    render: (updated_by) => (
      <EllipsisComponent maxWidth={100}>{updated_by}</EllipsisComponent>
    ),
  },
  {
    title: 'Role',
    dataIndex: 'role_code',
    key: 'role_code',
  },
  {
    title: 'Status',
    dataIndex: 'is_active',
    key: 'is_active',
  },
];

export const AdminManageUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [api, contextHolder] = notification.useNotification();

  const {
    data = [],
    isLoading,
    isRefetching,
    isError,
    refetch,
    error,
  } = useGetUsers();

  useEffect(() => {
    if (location.state === 'success') {
      return api.success({
        message: 'User invited',
        description: `User has been successfully invited.`,
        duration: 3,
      });
    } // eslint-disable-next-line
  }, []);

  if (isError) {
    return (
      <AdminErrorPage
        isRefetching={isRefetching}
        refetch={refetch}
        error={error.response?.data?.message || 'Error fetching users'}
      />
    );
  }

  const parsedData = () => {
    if (data) {
      return data.map((obj) => {
        return {
          ...obj,
          is_active: obj.is_active ? 'Active' : 'Inactive',
        };
      });
    }
    return [];
  };

  return (
    <AdminPageWrapper>
      {contextHolder}
      <TableComponent
        onRow={(record) => ({
          onClick: () => {
            navigate(`/admin/manage/users/${record.id}`);
          },
        })}
        loading={isLoading || isRefetching}
        tableName='Manage Users'
        width={1200}
        dataSource={parsedData()}
        total={data.length}
        columns={columns}
        ctaButtons={
          <ButtonComponent
            label='Invite User'
            onClick={() => {
              navigate('/admin/manage/users/add');
            }}
            appereance='primary'
            size='middle'
            style={{ maxWidth: 320 }}
            fullWidth
          />
        }
      />
    </AdminPageWrapper>
  );
};
